import {inject, Injectable} from '@angular/core';
import {NgbDate, NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {I18nService} from "./i18n/i18n.service";
import {registerLocaleData} from "@angular/common";
import {Formatter} from "../libs/table/items/formatter";
import {CurrencyDto} from "../repositories/dto/currency.dto";
import {CurrencyService} from "./currency.service";
import {isDefined, isFalse, isNumeric, isTrue, nonBlank} from "../libs/utils/epic.utils";
import {SearchFilterValueResponse} from "../repositories/dto/search/search-table.dto.res";
import {LoggingService} from "./logger/logging.service";
import {parsePhoneNumberFromString, PhoneNumber} from "libphonenumber-js";

@Injectable({providedIn: 'root'})
export class FormatService {

  private readonly i18n: I18nService = inject(I18nService);
  private readonly currencyService: CurrencyService = inject(CurrencyService);
  private readonly log: LoggingService = inject(LoggingService);

  public static readonly DIGITS_INFO: string = '1.2-5';
  public static readonly DEFAULT_FRACTION_DIGITS: number = 5;

  private currentLanguage!: string;

  constructor() {
    this.i18n.getCurrentLanguageCode().subscribe((lang) => {
      this.currentLanguage = lang;
      registerLocaleData(this.i18n.getCommonLocale(), lang);
    });
  }

  public num(value: number | undefined): string {
    if (isNumeric(value)) {
      return value.toLocaleString(this.currentLanguage);
    }
    return '';
  }

  public currency(value: number | undefined, currency?: CurrencyDto, fractionDigits?: number): string | undefined {
    return this.currencyService.format(value, currency, fractionDigits);
  }

  public amount(value: number | undefined, currencyCode: string | undefined, fractionalDigits: number): string | undefined {
    const currency: string = isDefined(currencyCode) ? currencyCode : CurrencyDto.NONE.code;
    return this.currencyService.formatAmount(value, currency, fractionalDigits);
  }

  public amountFormatter(currencyCode: string, fractionalDigits: number): Formatter {
    return new Formatter((value: number | undefined) => {
      return this.amount(value, currencyCode, fractionalDigits);
    });
  }

  public vatRate(value: string | number | undefined): string | undefined {
    if (isNumeric(value)) {
      return value + '%';
    }
    return undefined;
  }

  public date(value: Date | string | undefined): string | undefined {
    if (isDefined(value)) {
      let formatted;
      if (value instanceof Date) {
        formatted = this.i18n.getDateParser().fromDate(value);
      } else {
        formatted = this.i18n.getDateParser().fromLocalDate(value);
      }
      return formatted;
    }
    return undefined;
  }

  public dateTime(value: Date | string | undefined): string | undefined {
    if (isDefined(value)) {
      let formatted;
      if (value instanceof Date) {
        formatted = this.i18n.getDateParser().fromDate(value);
        formatted += ' ' + value.toLocaleTimeString();
      } else {
        formatted = this.i18n.getDateParser().fromLocalDate(value);
        formatted += ' ' + new Date(value).toLocaleTimeString();
      }
      return formatted;
    }
    return undefined;
  }

  // noinspection JSUnusedGlobalSymbols
  public utcDate(value: Date | undefined): string | undefined {
    if (value instanceof Date) {
      return String(value.getUTCDate()).padStart(2, '0') + '.' +
        String(value.getUTCMonth()).padStart(2, '0') + '.' +
        value.getUTCFullYear();
    }
    return undefined;
  }

  // noinspection JSUnusedGlobalSymbols
  public ngbDateFromDate(value: Date): NgbDate {
    return new NgbDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  // noinspection JSUnusedGlobalSymbols
  public dateFromNgbDate(value: NgbDateStruct | null | undefined): Date | undefined {
    if (isDefined(value)) {
      return new Date(Date.UTC(value.year, value.month - 1, value.day));
    }
    return undefined;
  }

  public buildTranslateFormatter(prefix?: string, suffix?: string): Formatter {
    return new Formatter((value) => {
      if (isDefined(value)) {
        const isFilter: boolean = value instanceof SearchFilterValueResponse;
        const v: string = isFilter ? String(value.name).toLowerCase() : String(value).toLowerCase();
        let key: string = prefix ? prefix + v : v;
        if (isDefined(suffix) && nonBlank(suffix)) {
          key = key + suffix;
        }
        let name: any = value;
        if (this.i18n.hasTranslationKey(key)) {
          name = this.i18n.translate(key);
        } else if (isFilter) {
          name = value.name;
        }
        return isFilter ? name + ' (' + value.count + ')' : name;
      }
      return value;
    });
  }

  public buildPercentFormatter(): Formatter {
    return new Formatter((value) => value + '%');
  }

  public buildDateFormatter(): Formatter {
    return new Formatter((value) => this.date(value));
  }

  public buildBoolYesNoEmptyFormatter(): Formatter {
    return new Formatter((value) => {
      const isFilter: boolean = value instanceof SearchFilterValueResponse;
      const v: any = isFilter ? value.name : value;
      const key: 'true' | 'false' | undefined = isTrue(v) ? 'true' : isFalse(v) ? 'false' : undefined;
      if (isDefined(key)) {
        const name: string = this.i18n.translate('search.filter.' + key);
        return isFilter ? name + ' (' + value.count + ')' : name;
      }
      this.log.warn('Translation not set for boolean-value-formatter "{}"', [v]);
      return '';
    });
  }

  // Expand to value (any) if required
  public buildPaymentFormatter(): Formatter {
    return new Formatter((value: SearchFilterValueResponse): string => {
      if (isTrue(value.name)) {
        return this.i18n.translate('payment.paid') + ' (' + value.count + ')';
      } else if (isFalse(value.name)) {
        return this.i18n.translate('payment.unpaid') + ' (' + value.count + ')';
      }
      this.log.warn('Translation not set for payment-formatter "{}"', [value.name]);
      return value.name + ' (' + value.count + ')';
    });
  }

  public buildNumberFormatter(): Formatter {
    return new Formatter((value: any | undefined) => {
      return isNumeric(value) ? this.num(value) : value;
    });
  }

  public buildPhoneNumberFormatter(): Formatter {
    return new Formatter((value: any) => this.phoneNumber(value));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public phoneNumber(value: any): any {
    if (isDefined(value) && typeof value === 'string') {
      const number: PhoneNumber | undefined = parsePhoneNumberFromString(value);
      return isDefined(number) && number.isValid() ? number.formatInternational() : value;
    }
    return value;
  }

  /**
   * Convert bytes to a human-readable format.
   * Example usage:
   * const fileSizeInBytes = 2323235;
   * const formattedSize = formatBytes(fileSizeInBytes);
   * formattedSize == Output: "2.22 MB"
   *
   * @param bytes - The size in bytes.
   * @param decimals - The number of decimal places to include in the formatted result.
   *                   Default is 2 decimal places.
   * @returns The size formatted as a human-readable string.
   */
  public formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) {
      return '0 Byte';
    }

    const k = 1024;
    const dm: number = decimals < 0 ? 0 : decimals;
    const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i: number = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(dm) + ' ' + sizes[i];
  }
}
